import { Link } from "react-router-dom";
import { reports } from "../api/client";
import ProgressBar from "../components/ProgressBar";
import { fmtEUR } from "../lib/auth";
import { useWidgetData } from "./data";
import type { WidgetComponentProps } from "./registry";

const today = new Date();

export default function MonatWidget({ config, size }: WidgetComponentProps) {
  // Month in the key: the cache persists across app restarts, so a fixed key
  // would flash last month's numbers after a month rollover.
  const summary = useWidgetData(
    `budgetSummary:${today.getFullYear()}-${today.getMonth() + 1}`,
    () => reports.budgetSummary(today.getFullYear(), today.getMonth() + 1),
  );
  if (!summary) return null;
  const show = (config.show as string) ?? "both";
  const ess = summary.essential ?? { budgetiert: 0, spent: 0, count: 0 };
  const life = summary.lifestyle ?? { budgetiert: 0, spent: 0, count: 0 };
  const cards: React.ReactNode[] = [];
  if (show !== "lifestyle") {
    cards.push(
      <SummaryCard key="ess" label="Alltagskosten" budget={ess.budgetiert} spent={ess.spent} accent="essential" />,
    );
  }
  if (show !== "essential") {
    cards.push(
      <SummaryCard key="life" label="Lifestyle" budget={life.budgetiert} spent={life.spent} accent="lifestyle" />,
    );
  }
  return (
    <section>
      <div className="flex items-baseline justify-between mb-2">
        <h2 className="section-title">Diesen Monat</h2>
        <Link to="/budget" className="text-xs text-brand-300 hover:underline">
          Budget →
        </Link>
      </div>
      <div
        className={
          "grid gap-3 " + (size === "full" && cards.length > 1 ? "md:grid-cols-2" : "")
        }
      >
        {cards}
      </div>
    </section>
  );
}

function SummaryCard({
  label,
  budget,
  spent,
  accent,
}: {
  label: string;
  budget: number;
  spent: number;
  accent: "essential" | "lifestyle";
}) {
  const pct = budget > 0 ? Math.min(100, (spent / budget) * 100) : 0;
  const overspent = spent > budget;
  const remaining = Math.max(0, budget - spent);
  const accentColor = accent === "essential" ? "#0d9488" : "#b45309";
  return (
    <article className="card p-5">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="text-base font-semibold text-ink-100">{label}</h3>
        <span className={overspent ? "badge-bad" : pct > 80 ? "badge-warn" : "badge-good"}>
          {pct.toFixed(0)} % verbraucht
        </span>
      </div>
      <div className="mt-2 flex items-baseline gap-2">
        <span className="text-2xl font-semibold num text-ink-50">{fmtEUR(spent)}</span>
        <span className="text-sm text-ink-400">von {fmtEUR(budget)}</span>
      </div>
      <div className="mt-2">
        <ProgressBar
          value={Math.min(spent, budget)}
          total={Math.max(budget, 1)}
          height={8}
          segments={[{ label, value: Math.min(spent, budget), color: accentColor }]}
        />
      </div>
      <div className="mt-2 text-xs num text-ink-400">
        Übrig: <span className="text-ink-200">{fmtEUR(remaining)}</span>
      </div>
    </article>
  );
}
